// Helper to get API base URL
const getApiUrl = () => '/api';

export interface JiraTicket {
  id: number;
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  priority: string;
  issueType: string;
  issueTypeIcon: string;
  project: {
    key: string;
    name: string;
  };
  description: string | null;
  created: string;
  updated: string;
  dueDate: string | null;
  url: string;
}

export interface JiraTicketsResponse {
  connected: boolean;
  message?: string;
  total?: number;
  startAt?: number;
  maxResults?: number;
  issues?: JiraTicket[];
}

export interface JiraConnectionStatus {
  connected: boolean;
  accountId: string | null;
  cloudId: string | null;
  tokenExpiry: string | null;
}

/**
 * Initiate Jira OAuth flow
 * Returns authorization URL that should be opened in a new window/tab
 */
export async function initiateJiraAuth(token: string): Promise<{ authUrl: string }> {
  const response = await fetch(`${getApiUrl()}/jira/auth/initiate`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to initiate Jira authentication');
  }

  return response.json();
}

/**
 * Get user's Jira tickets
 */
export async function getMyJiraTickets(
  token: string, 
  options: { maxResults?: number; startAt?: number } = {}
): Promise<JiraTicketsResponse> {
  const params = new URLSearchParams();
  if (options.maxResults) params.append('maxResults', options.maxResults.toString());
  if (options.startAt) params.append('startAt', options.startAt.toString());

  const response = await fetch(
    `${getApiUrl()}/jira/tickets${params.toString() ? `?${params.toString()}` : ''}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch Jira tickets');
  }

  return response.json();
}

/**
 * Get user's active Jira tickets (In Progress, updated in last 7 days)
 */
export async function getActiveJiraTickets(
  token: string,
  date: Date, 
  options: { maxResults?: number; startAt?: number } = {}
): Promise<JiraTicketsResponse> {
  const params = new URLSearchParams();
  params.append('date', date.toISOString());
  if (options.maxResults) params.append('maxResults', options.maxResults.toString());
  if (options.startAt) params.append('startAt', options.startAt.toString());

  const response = await fetch(
    `${getApiUrl()}/jira/tickets/active${params.toString() ? `?${params.toString()}` : ''}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch active Jira tickets');
  }

  return response.json();
}

/**
 * Get Jira connection status
 */
export async function getJiraStatus(token: string): Promise<JiraConnectionStatus> {
  const response = await fetch(`${getApiUrl()}/jira/status`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to get Jira connection status');
  }

  return response.json();
}

/**
 * Disconnect Jira account
 */
export async function disconnectJira(token: string): Promise<{ message: string }> {
  const response = await fetch(`${getApiUrl()}/jira/disconnect`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to disconnect Jira account');
  }

  return response.json();
}
